import { collection, doc, getDocs, getFirestore, serverTimestamp, setDoc } from 'firebase/firestore';
import { resolveCaseNumber } from '../view_web_eval/cloudEvalAdapters';

/**
 * Report scoring queue for cloud evals.
 *
 * Whether an eval has been scored lives in its own `eval_reviews` doc (keyed
 * by evaluation id), NOT on the cloud_eval_results doc: vasi_functions
 * reprocessing rewrites that doc with a non-merge set(), which would wipe it.
 * For now the review doc is a placeholder that only records that the eval was
 * scored; the report scoring tools can fill in scores on the same doc later.
 */
export const EVAL_REVIEWS_COLLECTION = 'eval_reviews';
export const REVIEW_SOURCE_CLOUD = 'cloud_eval';
export const REVIEW_STATUS_SCORED = 'scored';

// Fired after an eval is marked scored so the nav badge can refresh.
export const EVAL_REVIEWS_CHANGED_EVENT = 'vasi:eval-reviews-changed';

// Hemispheres Neuro's own org: its evals are internal testing, not client work.
export const INTERNAL_ORGANIZATION_IDS = ['D4hmtA7htq9tXdFUFhUC'];

function startedMillis(evaluation) {
  const millis = Date.parse(evaluation?.eval_started ?? '');
  return Number.isNaN(millis) ? Number.POSITIVE_INFINITY : millis;
}

/**
 * Unscored client evals, oldest first. The org comes from the eval's client:
 * cloud_eval_results.organization holds the org type ("vasi"), not its id.
 * Evals whose client record is missing can't be opened, so they're skipped.
 */
export function buildReportQueue({
  evaluations = [],
  clients = [],
  organizations = [],
  scoredIds = new Set(),
  excludeOrganizationIds = INTERNAL_ORGANIZATION_IDS,
} = {}) {
  const clientsById = new Map(clients.map((client) => [client.id, client]));
  const organizationsById = new Map(organizations.map((organization) => [organization.id, organization]));
  const excluded = new Set(excludeOrganizationIds);

  return evaluations
    .filter((evaluation) => !scoredIds.has(evaluation.id))
    .map((evaluation) => {
      const client = clientsById.get(evaluation.client);
      const organizationId = client?.organization_id ?? null;
      return {
        evaluationId: evaluation.id,
        clientId: client?.id ?? null,
        clientName: client?.Name ?? '',
        organizationId,
        organizationName: organizationsById.get(organizationId)?.name ?? '',
        caseNumber: resolveCaseNumber(evaluation),
        evalStarted: evaluation.eval_started ?? null,
        startedMillis: startedMillis(evaluation),
      };
    })
    .filter((item) => item.clientId && item.organizationId && !excluded.has(item.organizationId))
    .sort((left, right) => left.startedMillis - right.startedMillis
      || left.evaluationId.localeCompare(right.evaluationId));
}

function snapshotDocs(snapshot) {
  return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
}

export async function loadReportQueue({ db = getFirestore() } = {}) {
  const [evaluations, clients, organizations, reviews] = await Promise.all([
    getDocs(collection(db, 'cloud_eval_results')),
    getDocs(collection(db, 'clients')),
    getDocs(collection(db, 'organizations')),
    getDocs(collection(db, EVAL_REVIEWS_COLLECTION)),
  ]);

  const scoredIds = new Set(
    snapshotDocs(reviews)
      .filter((review) => review.status === REVIEW_STATUS_SCORED)
      .map((review) => review.id)
  );

  return buildReportQueue({
    evaluations: snapshotDocs(evaluations),
    clients: snapshotDocs(clients),
    organizations: snapshotDocs(organizations),
    scoredIds,
  });
}

/**
 * Record that an eval was scored. Merges, so scores the report tools add to
 * this doc later are never wiped by marking it done again.
 */
export async function markEvalScored({
  evaluationId,
  clientId = null,
  organizationId = null,
  caseNumber = null,
  userId = null,
  db = getFirestore(),
} = {}) {
  if (!evaluationId) {
    throw new Error('Cannot mark an eval scored without an evaluation id.');
  }
  await setDoc(doc(db, EVAL_REVIEWS_COLLECTION, evaluationId), {
    evaluationId,
    source: REVIEW_SOURCE_CLOUD,
    clientId,
    organizationId,
    caseNumber,
    status: REVIEW_STATUS_SCORED,
    scoredBy: userId,
    scoredAt: serverTimestamp(),
  }, { merge: true });
}

export function reportQueuePath(evaluationId) {
  return evaluationId ? `/report/${encodeURIComponent(evaluationId)}` : '/report';
}
